import { CircularProgress, Grid } from "@mui/material";
import React, { createContext, useEffect, useRef, useState } from "react";

import Header from "../../Layouts/Components/Header";
import FiltersHeader from "../shared/filters/FiltersHeader";
import TableHeader from "../shared/table/TableHeader";
import ReportTable from "./ReportTable";

import "./reporting.css";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  changeSearchCriteria,
  getInitialReportData,
  getReportData,
  resetSearchCriteria,
  saveCustomReport,
  updateCustomReport,
} from "./state/actions";
import { useKeycloak } from "@react-keycloak/web";
import AllFilters from "../shared/filters/AllFilters";
import PrimaryButton from "../shared/PrimaryButton";
import useSnackBar from "../Hooks/useSnackBar";

export const ReportContext = createContext(null);

function extractGroupLevels(obj, groupLevelsSet) {
  if (obj && typeof obj === "object") {
    if (obj.hasOwnProperty("groupLevel")) {
      groupLevelsSet.add(obj.groupLevel);
    }
    for (const key in obj) {
      extractGroupLevels(obj[key], groupLevelsSet);
    }
  }
}

function getGroupLevelsInitialValue(data) {
  const uniqueGroupLevels = new Set();
  extractGroupLevels(data, uniqueGroupLevels);
  const uniqueGroupLevelsArray = Array.from(uniqueGroupLevels);

  return uniqueGroupLevelsArray;
}

function getOrderByInitialValue(data) {
  return data?.map((string) => string.split("_")[0]) || [];
}

function extractColumnsDirection(arr) {
  return arr?.reduce((obj, string) => {
    const [key, value] = string.split("_");
    obj[key] = value === "d";
    return obj;
  }, {});
}

function getOrderByColumnsOrderDirection(data) {
  return extractColumnsDirection(data) || {};
}

const ReportDetails = () => {
  const { reportId, type } = useParams();
  const isCustomReport = type !== "corzap";

  const { error, snackbar, success } = useSnackBar();

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const { keycloak } = useKeycloak();
  const userId = keycloak.tokenParsed.preferred_username;
  const id = !isCustomReport ? "" : userId;

  const loadingReportData = useSelector(
    (state) => state.reporting.loadingReportData
  );
  const reportData = useSelector((state) => state.reporting.reportData);
  const isEmpty = Object.keys(reportData).length === 0;
  const searchCriteria = useSelector((state) => state.reporting.searchCriteria);
  const totalRows = reportData?.totalElement;

  const allColumns = (
    reportData?.allColumns !== undefined
      ? reportData?.allColumns
      : reportData?.columns
  )?.map((column) => ({
    ...column,
    id: column.key,
    name: column.title,
  }));

  const [columnsOrderDirection, setColumnsOrderDirection] = useState({});

  const customizationRef = useRef(null);

  const [columns, setColumns] = useState([
    ...(reportData?.columns || []).map((column) => ({
      ...column,
      id: column.key,
      name: column.title,
    })),
  ]);

  const [anchorEl, setAnchorEl] = useState(null);

  const pageNumber = +searchParams.get("pageNumber") || 1;
  const pageSize = +searchParams.get("pageSize") || 10;
  const openAllFilters = searchParams.get("allFilters") !== null;
  const isNewReport = searchParams.get("new") !== null;
  const dateTimeFrom = searchParams.get("dateTimeFrom");
  const dateTimeTo = searchParams.get("dateTimeTo");

  const initialSearchCriteria = {
    ...searchCriteria,
    dateTimeFrom: isNewReport ? dateTimeFrom : searchCriteria.dateTimeFrom,
    dateTimeTo: isNewReport ? dateTimeTo : searchCriteria.dateTimeTo,
  };

  const headLine = `Report | ${reportId}`;

  const navigate = useNavigate();
  const [savingReport, setSavingReport] = useState(false);

  const [currentGroupBy, setCurrentGroupBy] = useState(
    getGroupLevelsInitialValue(reportData?.dto)
  );
  const [currentOrderBy, setCurrentOrderBy] = useState(
    getOrderByInitialValue(reportData?.orderBy)
  );

  const handleFiltersChange = (values) => {
    dispatch(changeSearchCriteria(values));
  };

  const handleShowColumns = () => {
    setAnchorEl(customizationRef.current);
  };

  const handleToggleRenderAllFilters = () => {
    const value = !openAllFilters;

    if (value) {
      searchParams.set("allFilters", true);
      setSearchParams(searchParams);
    } else {
      searchParams.delete("allFilters");
      setSearchParams(searchParams);
    }
  };

  const handleResetFilters = () => {
    dispatch(resetSearchCriteria());
  };

  const _success = (msg) => {
    setSavingReport(false);
    success(msg);
  };
  const _error = (msg) => {
    setSavingReport(false);
    error(msg);
  };

  const handleSaveReport = (dimensions) => {
    const data = {
      messageType: searchCriteria.type || null,
      dimensions: columns.map((column) => column.key),
      groupBy: currentGroupBy,
      orderBy: currentOrderBy.map(
        (ob) => `${ob}_${!!columnsOrderDirection[ob] ? "d" : "a"}`
      ),
    };

    const nameWithoutSpaces = reportId.replace(/\s/g, "-");

    const filters = {
      pageSize,
      pageNumber,
      searchCriteria: {
        ...searchCriteria,
      },
    };

    setSavingReport(true);

    if (Array.isArray(dimensions)) {
      data.dimensions = dimensions;
    }

    if (isCustomReport && !isNewReport) {
      dispatch(
        updateCustomReport(
          id,
          nameWithoutSpaces,
          data,
          filters,
          _success,
          _error
        )
      );
    } else if (!isCustomReport) {
      // const uniqueReportName = `${reportId}_${Math.random()
      //   .toString(36)
      //   .substring(2, 15)}`;

      const uniqueReportName = `${reportId}_duplicate`;
      dispatch(
        saveCustomReport(
          userId,
          uniqueReportName,
          data,
          filters,
          (msg) => {
            _success(msg);
            navigate(`/reports/custom/${uniqueReportName}`);
          },
          _error,
          false
        )
      );
    } else {
      dispatch(
        saveCustomReport(
          id,
          nameWithoutSpaces,
          data,
          filters,
          (msg) => {
            _success(msg);
            navigate(`/reports/custom/${nameWithoutSpaces}`);
          },
          _error,
          false
        )
      );
    }
  };

  const handleColumnsChange = (items) => {
    setColumns([...items]);

    handleSaveReport(items.map((column) => column.key));
  };

  useEffect(() => {
    if (!isNewReport) {
      dispatch(
        getReportData(
          id,
          reportId,
          {
            pageSize,
            pageNumber,
            searchCriteria: {
              ...searchCriteria,
            },
          },
          success,
          error
        )
      );
    } else {
      dispatch(
        getInitialReportData(
          {
            pageSize,
            pageNumber,
            searchCriteria: {
              ...searchCriteria,
              dateTimeFrom,
              dateTimeTo,
            },
          },
          success,
          error
        )
      );
    }
  }, [
    id,
    reportId,
    pageNumber,
    pageSize,
    searchCriteria,
    isNewReport,
    dateTimeFrom,
    dateTimeTo,
    dispatch,
    error,
    success,
  ]);

  useEffect(() => {
    setCurrentGroupBy(getGroupLevelsInitialValue(reportData?.dto));
  }, [reportData?.dto?.groupSegment?.groupLevel]);

  useEffect(() => {
    setCurrentOrderBy(getOrderByInitialValue(reportData?.orderBy));
    setColumnsOrderDirection(
      getOrderByColumnsOrderDirection(reportData?.orderBy)
    );
  }, [reportData]);

  useEffect(() => {
    setColumns([
      ...(reportData?.columns || []).map((column) => ({
        ...column,
        id: column.key,
        name: column.title,
      })),
    ]);
  }, [reportData?.columns]);

  return (
    <>
      {snackbar}
      <ReportContext.Provider value={{}}>
        <Header headLine={headLine}></Header>
        {openAllFilters ? (
          <AllFilters
            {...{
              filters: initialSearchCriteria,
              headLine,
              handleFiltersChange,
              handleToggleRenderAllFilters,
              handleResetFilters,
              type: "ranges",
              changeSearchParams: isNewReport,
            }}
          />
        ) : (
          <>
            <FiltersHeader
              {...{
                filters: initialSearchCriteria,
                handleFiltersChange,
                handleToggleRenderAllFilters,
                handleResetFilters,
                type: "ranges",
                changeSearchParams: isNewReport,
                actions: (
                  <div style={{ marginLeft: "auto" }}>
                    {(isNewReport || !isCustomReport) && (
                      <PrimaryButton onClick={handleSaveReport}>
                        {savingReport ? (
                          <CircularProgress
                            size={16}
                            sx={{ marginRight: "1rem" }}
                          />
                        ) : null}
                        Save
                      </PrimaryButton>
                    )}
                  </div>
                ),
              }}
              dateOnly
            />
            <Grid container>
              <Grid
                item
                xs={12}
                sx={{
                  padding: 5,
                  background: "#F8FAFB",
                  overflow: "hidden",
                }}
              >
                {!isEmpty ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TableHeader
                        reportHeader
                        defaultColumns={allColumns}
                        allColumns={allColumns}
                        inUseColumns={columns}
                        numberOfRows={totalRows || 0}
                        numberOfSelectedRows={0}
                        handleColumnsChange={handleColumnsChange}
                        handlePrint={() => {}}
                        handleMail={() => {}}
                        handleViewMessages={() => {}}
                        handleUnselectAll={() => {}}
                        {...{ anchorEl, setAnchorEl, handleShowColumns }}
                        currentGroupBy={currentGroupBy}
                        setCurrentGroupBy={setCurrentGroupBy}
                        currentOrderBy={currentOrderBy}
                        setCurrentOrderBy={setCurrentOrderBy}
                        setColumns={setColumns}
                        columnsOrderDirection={columnsOrderDirection}
                        setColumnsOrderDirection={setColumnsOrderDirection}
                        showGroupBy
                      />
                    </Grid>

                    <Grid item xs={12} ref={null}>
                      <ReportTable reportData={reportData} columns={columns} />
                    </Grid>
                  </Grid>
                ) : (
                  <div style={{ textAlign: "center", marginTop: "50px" }}>
                    {loadingReportData ? <CircularProgress /> : "No data found"}
                  </div>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </ReportContext.Provider>
    </>
  );
};

export default ReportDetails;
